import * as React from 'react';

import { Flex, flameTheme } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { IconDesktop } from '@lightspeed/flame/Icon/Desktop';
import { IconExternalLink } from '@lightspeed/flame/Icon/ExternalLink';
import { Text, TextLink, Heading4 } from '@lightspeed/flame/Text';
import capitalize from 'lodash/capitalize';
import { useSelector, useDispatch } from 'react-redux';

import { useGetAppDownloadUrl } from 'pages/views/devices/hooks/use-get-app-download-url';
import { useGetCfd } from 'pages/views/devices/hooks/use-get-cfd';
import { useGetPrinter } from 'pages/views/devices/hooks/use-get-printer';
import Client from 'pages/views/devices/socketClient';
import { STATUS } from 'pages/views/devices/socketClient/types';
import {
  PRINTER_KINDS,
  PRINTER_CONNECTIONS,
  CFD_MODAL_TYPE,
  Printer,
  PLATFORMS,
} from 'pages/views/devices/types';
import { URL } from 'pages/views/devices/utils/help-urls';
import {
  PROTOCOL,
  PROTOCOL_IFRAME,
  APP_NAME,
  setCfdModal,
  getDetectedPlatform,
} from 'pages/views/devices/utils/helper';

import { DeviceCard } from '../components/DeviceCard';
import { PrinterImage } from '../components/PrinterImage';
import { StatusLabel } from '../components/StatusLabel';
import { DeviceIconWrapper, verticalAlign } from '../device.styles';
import { DevicesSidebar } from '../DevicesSidebar';
import { handleConnect } from '../utils/handle-hub-connect';

import { CfdConnectionModal } from './CfdConnectionModal';
import { CfdSettingsModal } from './CfdSettingsModal';

function usePrevious(value: TSFixMe) {
  const ref = React.useRef();

  React.useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

interface ConnectionStateTexts {
  hasDevice?: string | JSX.Element;
  connected: string | JSX.Element;
  disconnected: string | JSX.Element;
}

const DevicesPage: React.FC = () => {
  const dispatch = useDispatch();
  const { platform } = getDetectedPlatform();
  const { appDownloadLinks } = useGetAppDownloadUrl();
  const selectedModal = useSelector(state => state.hub.cfdModal);
  const connectionStatus = useSelector(state => state.hub.connection.ably);
  const isHubConnected = connectionStatus === STATUS.CONNECTED;
  const { isCfdConnected, cfdConnectionStatus, cfdInfo, connectionMethodCopy } = useGetCfd();
  const { isPrinterConnected: isReceiptPrinterConnected, printer: receiptPrinter } = useGetPrinter(
    PRINTER_KINDS.RECEIPT
  );
  const { isPrinterConnected: isLabelPrinterConnected, printer: labelPrinter } = useGetPrinter(
    PRINTER_KINDS.LABEL
  );
  const [devicesCount, setDevicesCount] = React.useState(0);
  const previousCFDConnectionStatus = usePrevious(isCfdConnected);
  const { space, colors } = flameTheme;

  const RECEIPT_PRINTER_URL = `${PROTOCOL}${Client.getCFDParams()}&routeTo=/printers/RECEIPT`;
  const LABEL_PRINTER_URL = `${PROTOCOL}${Client.getCFDParams()}&routeTo=/printers/LABEL`;

  const downloadRef = React.useRef<HTMLAnchorElement>(null);
  const openRef = React.useRef<HTMLAnchorElement>(null);

  React.useEffect(() => {
    if (connectionStatus === STATUS.DISCONNECTED) {
      setDevicesCount(0);
      return;
    }
    const connectedDevices = [
      isReceiptPrinterConnected,
      isLabelPrinterConnected,
      isCfdConnected,
    ].filter(Boolean).length;
    setDevicesCount(connectedDevices);
  }, [isReceiptPrinterConnected, isLabelPrinterConnected, connectionStatus, isCfdConnected]);

  /**
   * This will handle the case where the CFD setup modal is opened while connecting.
   * Once we connect the application it'll close the modal for us.
   */
  React.useEffect(() => {
    if (!previousCFDConnectionStatus && isCfdConnected && selectedModal === CFD_MODAL_TYPE.SETUP) {
      setCfdModal(undefined);
    }
  }, [isCfdConnected, previousCFDConnectionStatus, selectedModal]);

  const handleModalOpen = (type: CFD_MODAL_TYPE) => () => {
    setCfdModal(type);
  };

  const getBodyState = ({ connected, disconnected, hasDevice }: ConnectionStateTexts) => {
    if (isHubConnected && hasDevice) return hasDevice;
    if (connectionStatus === STATUS.DISCONNECTED) return disconnected;
    return connected;
  };

  const getPrinterInformation = (
    { fallback }: { fallback: string },
    printer: Printer,
    isPrinterConnected: boolean
  ): string | JSX.Element =>
    isPrinterConnected ? (
      <span data-isolate>
        <var>
          {capitalize(printer.brand)} {printer.model}
        </var>{' '}
        using{' '}
        {printer.connection === PRINTER_CONNECTIONS.NETWORK
          ? capitalize(printer.connection)
          : printer.connection}
      </span>
    ) : (
      fallback
    );

  const getCFDInformation = () => {
    const additionalText = isCfdConnected ? ` using ${connectionMethodCopy}` : '';
    return (
      <>
        {cfdInfo}
        {additionalText}
      </>
    );
  };

  const devicesConnected = `${devicesCount === 1 ? 'device' : 'devices'} connected`;

  const getConnectButtonOptions = (
    isPrinterConnected: boolean,
    printerType: PRINTER_KINDS.RECEIPT | PRINTER_KINDS.LABEL
  ) => {
    const connectButtonOptions = {
      isVisible: !isPrinterConnected,
      isDisabled: connectionStatus === STATUS.DISCONNECTED,
      label: 'Connect',
      variant: 'secondary',
      href: printerType === PRINTER_KINDS.LABEL ? LABEL_PRINTER_URL : RECEIPT_PRINTER_URL,
      color: 'white',
      target: PROTOCOL_IFRAME,
      onClick: () => {
        setTimeout(() => {
          checkWindowVisibilityState();
        }, 500);
      },
    };

    return connectButtonOptions;
  };

  const checkWindowVisibilityState = () => {
    if (document.hasFocus()) {
      downloadRef?.current?.click();
    }
    openRef?.current?.click();
  };

  const getPrinterLinkOptions = (
    isPrinterConnected: boolean,
    printerType: PRINTER_KINDS.RECEIPT | PRINTER_KINDS.LABEL
  ) => {
    const isReceiptPrinter = printerType === PRINTER_KINDS.RECEIPT;

    let PRINTER_URL;
    isReceiptPrinter ? (PRINTER_URL = RECEIPT_PRINTER_URL) : (PRINTER_URL = LABEL_PRINTER_URL);

    let HC_ARTICLE;
    isReceiptPrinter ? (HC_ARTICLE = URL.SUPPORTED_DEVICES) : (HC_ARTICLE = URL.LABEL_PRINTER);

    return {
      label: isPrinterConnected ? 'View in Lightspeed Hub' : 'Learn more',
      url: isPrinterConnected ? PRINTER_URL : HC_ARTICLE,
      target: isPrinterConnected ? PROTOCOL_IFRAME : '_blank',
    };
  };

  return (
    <>
      <CfdConnectionModal
        isModalOpen={selectedModal === CFD_MODAL_TYPE.SETUP}
        connectionStatus={isCfdConnected ? STATUS.CONNECTED : cfdConnectionStatus}
      />
      <CfdSettingsModal isModalOpen={selectedModal === CFD_MODAL_TYPE.SETTINGS} />
      <Flex flexDirection="column" p={3}>
        <Text fontSize="21px" color={colors.black} fontWeight="bold" mb={1}>
          Devices
        </Text>
        {!devicesCount ? (
          <Text color={colors.textDimmed}>No devices connected</Text>
        ) : (
          <Text color={colors.textDimmed} key="localizejs-connected-devices">
            <var>{devicesCount}</var> {devicesConnected}
          </Text>
        )}
        <Flex flexDirection="column">
          {!window.isGriffin() ? (
            <Flex flexDirection="column" data-test="devices-sidebar" mt={6}>
              <DevicesSidebar />
            </Flex>
          ) : null}
          <Divider py={5} color={colors['gray-100']} />
          <Flex>
            <Flex flexDirection="column" maxWidth="240px" flex="0 1 auto" minWidth="225px" mr={7}>
              <Heading4 mb={1} color={colors['gray-850']}>
                Connected Hardware
              </Heading4>
              <Text mb={1}>
                Adding a device helps you sell to new customers online, on mobile apps, through
                social networks, and in-person.
              </Text>
              <TextLink
                color={colors['blue-500']}
                fontWeight="bold"
                href={URL.SUPPORTED_DEVICES}
                target="_blank"
                mb={1}
                width="fit-content"
              >
                Learn more
              </TextLink>
            </Flex>
            <Flex flexDirection="column" flex="1 0 auto" ml={7}>
              {!window.isGriffin() ? (
                <>
                  <DeviceCard
                    title="Receipt printer"
                    status={
                      isReceiptPrinterConnected && (
                        <StatusLabel size="medium" status={receiptPrinter!.status} />
                      )
                    }
                    body={getBodyState({
                      disconnected: getPrinterInformation(
                        { fallback: `Requires ${APP_NAME}` },
                        receiptPrinter!,
                        isReceiptPrinterConnected
                      ),
                      connected: getPrinterInformation(
                        { fallback: 'No device connected' },
                        receiptPrinter!,
                        isReceiptPrinterConnected
                      ),
                      hasDevice: getPrinterInformation(
                        { fallback: '' },
                        receiptPrinter!,
                        isReceiptPrinterConnected
                      ),
                    })}
                    link={getPrinterLinkOptions(isReceiptPrinterConnected, PRINTER_KINDS.RECEIPT)}
                    icon={
                      <PrinterImage
                        model={receiptPrinter?.model}
                        size="60px"
                        connected={isReceiptPrinterConnected}
                      />
                    }
                    buttonOptions={getConnectButtonOptions(
                      isReceiptPrinterConnected,
                      PRINTER_KINDS.RECEIPT
                    )}
                  />
                  <DeviceCard
                    title="Label printer"
                    status={
                      isLabelPrinterConnected && (
                        <StatusLabel size="medium" status={labelPrinter!.status} />
                      )
                    }
                    body={getBodyState({
                      disconnected: getPrinterInformation(
                        { fallback: `Requires ${APP_NAME}` },
                        labelPrinter!,
                        isLabelPrinterConnected
                      ),
                      connected: getPrinterInformation(
                        { fallback: 'No device connected' },
                        labelPrinter!,
                        isLabelPrinterConnected
                      ),
                      hasDevice: getPrinterInformation(
                        { fallback: '' },
                        labelPrinter!,
                        isLabelPrinterConnected
                      ),
                    })}
                    link={getPrinterLinkOptions(isLabelPrinterConnected, PRINTER_KINDS.LABEL)}
                    icon={
                      <PrinterImage
                        model={labelPrinter?.model}
                        size="60px"
                        connected={isLabelPrinterConnected}
                      />
                    }
                    buttonOptions={getConnectButtonOptions(
                      isLabelPrinterConnected,
                      PRINTER_KINDS.LABEL
                    )}
                  />
                </>
              ) : null}
              <TextLink
                href={appDownloadLinks?.[platform as PLATFORMS]}
                ref={downloadRef}
                target={PROTOCOL_IFRAME}
                download
                style={{ display: 'none' }}
                onClick={() =>
                  dispatch({
                    type: 'HUB_UPDATE_DOWNLOAD_STATUS',
                    payload: { hasStartedHubDownload: true },
                  })
                }
              >
                Redirect to {APP_NAME} download
              </TextLink>
              <TextLink
                ref={openRef}
                onClick={handleConnect}
                href={`${PROTOCOL}${Client.getCFDParams()}`}
                target={PROTOCOL_IFRAME}
                style={{ display: 'none' }}
              >
                Open {APP_NAME}
              </TextLink>
              <DeviceCard
                title="Customer Facing Display"
                status={
                  isCfdConnected && (
                    <StatusLabel
                      size="medium"
                      status={isCfdConnected ? STATUS.CONNECTED : cfdConnectionStatus}
                    />
                  )
                }
                body={getCFDInformation()}
                link={{
                  label: 'Learn more',
                  url: URL.CFD,
                  target: '_blank',
                }}
                icon={
                  <DeviceIconWrapper>
                    <IconDesktop size="32px" color="gray-500" />
                  </DeviceIconWrapper>
                }
                buttonOptions={{
                  isVisible: true,
                  isDisabled: false,
                  label: isCfdConnected ? 'View Settings' : 'Connect',
                  onClick: isCfdConnected
                    ? handleModalOpen(CFD_MODAL_TYPE.SETTINGS)
                    : handleModalOpen(CFD_MODAL_TYPE.SETUP),
                  variant: 'secondary',
                }}
              />
              {!window.isGriffin() ? (
                <Flex alignItems="center">
                  <TextLink
                    color={colors.secondary}
                    href={URL.SUPPORTED_DEVICES}
                    target="_blank"
                    fontWeight="bold"
                  >
                    See a list of supported devices{' '}
                    <IconExternalLink
                      size={space[2] as string}
                      css={verticalAlign}
                      color={colors.secondary}
                    />
                  </TextLink>
                </Flex>
              ) : null}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export { DevicesPage };
